import React from 'react';
import UnitIcon from '@components/game/objects/UnitIcon';
import EnergyDisplay from '@components/ui/game/energy-display/EnergyDisplay';
import HPRing from '@components/ui/game/hp-ring/HPRing';
import { getEffectiveStat } from '@utils/statCalculations';
import './UnitCard.css';

/**
 * UnitCard - Unified card component for displaying units
 * Used in DeployableUnitsPanel and DeployedUnitsTab
 *
 * @param {object} unit - Unit data object
 * @param {string} size - 'small' (70px) or 'medium' (140px)
 * @param {boolean} isSelected - Whether the card is selected
 * @param {string} teamClass - 'own', 'ally', or 'enemy'
 * @param {number} initiativeOrder - Badge number for initiative order (optional)
 * @param {boolean} showHPRing - Show HP ring around unit icon
 * @param {boolean} showEnergyCost - Show energy cost at bottom
 * @param {function} onClick - Click handler
 */
const UnitCard = ({
  unit,
  size = 'small',
  isSelected = false,
  teamClass = 'own',
  initiativeOrder = null,
  showHPRing = false,
  showEnergyCost = false,
  onClick = null
}) => {
  if (!unit) {
    console.warn('[UnitCard] No unit data provided');
    return null;
  }

  // Get energy cost from unit template
  const energyCost = unit.unit_template?.energy_deploy_cost;

  // Build class names
  const cardClasses = [
    'unit-card',
    `unit-card-${size}`,
    `team-${teamClass}`,
    isSelected ? 'selected' : ''
  ].filter(Boolean).join(' ');

  // Get display name based on size
  // Small: just #ID, Medium: full name (which includes template name)
  const displayName = size === 'medium'
    ? (unit.unit_template?.name || unit.name || `Unit #${unit.id}`)
    : null;

  return (
    <div className={cardClasses} onClick={onClick}>
      {/* Initiative order badge */}
      {initiativeOrder !== null && (
        <span className="unit-card-badge">{initiativeOrder}</span>
      )}

      {/* Unit image - wrapped with HPRing if showHPRing is true */}
      <div className="unit-card-image">
        {showHPRing ? (
          <HPRing
            currentHP={unit.hp}
            maxHP={getEffectiveStat(unit, 'max_hp')}
          >
            <UnitIcon
              unitData={unit}
              size={size === 'medium' ? 'big' : 'small'}
              clickable={false}
            />
          </HPRing>
        ) : (
          <UnitIcon
            unitData={unit}
            size={size === 'medium' ? 'big' : 'small'}
            clickable={false}
          />
        )}
      </div>

      {/* Name and/or ID */}
      <div className="unit-card-label">
        {displayName && <span className="unit-card-name">{displayName}</span>}
        <span className="unit-card-id">#{unit.id}</span>
      </div>

      {/* Energy Cost */}
      {showEnergyCost && energyCost != null && (
        <div className="unit-card-energy">
          <EnergyDisplay value={energyCost} size="tiny" />
        </div>
      )}
    </div>
  );
};

export default UnitCard;
